import {
  breakpointSizes,
  getBreakpointDown,
  getFontCss,
  HoverableLink,
  styleConstants,
  Tag,
  TagSizes,
  themeConstants,
} from '@paywithextend/components'
import { useQuery } from '@tanstack/react-query'
import {
  base_colorContentBorder,
  base_colorRaisedSurface,
} from 'ext-ui/style/colors'
import { spacing } from 'ext-ui/style/spacing'
import { LocationState } from 'history'
import moment from 'moment'
import React, { ReactNode, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router'
import {
  selectExpenseReportIdFromRoute,
  selectManagerReviewExpenseReportIdFromRoute,
  selectMyWalletReimbursementIdFromRoute,
} from 'src/app/ExpenseReport/redux/expenseReport.selectors'
import { BreadcrumbPathComponent } from 'src/components/BreadcrumbsTopnav/BreadcrumbsTopnav'
import PopupMenu from 'src/components/PopupMenu/PopupMenu'
import ProfilePopupMenu from 'src/components/ProfilePopupMenu/ProfilePopupMenu'
import {
  sidenavCollapsedWidth,
  sidenavExpandedWidth,
} from 'src/components/SidenavWrapper/sidenav.constants'
import SvgSearchTopBar from 'src/components/Svgs/SvgSearchTopBar'
import TestId from 'src/components/TestId/TestId'
import UserAvatar from 'src/components/UserAvatar/UserAvatar'
import { AvatarSizes } from 'src/enums'
import { useExtendLocation } from 'src/hooks/locationHooks'
import { useTranslate } from 'src/localization/Trns'
import { UserFeature } from 'src/models/user.models'
import { AppLocation } from 'src/navigation/appLocations'
import Lnk from 'src/navigation/Lnk'
import {
  billPayAllUrl,
  budgetsAllBudgetsUrl,
  budgetsNeedsApprovalUrl,
  cardFeedsUrl,
  cardsPhysicalCardsUrl,
  departmentsUrl,
  expenseCategoriesAccountingUrl,
  expenseCategoriesAdditionalFieldsUrl,
  expensesExpenseReportsUrl,
  managerReviewReimbursementsURL,
  managerReviewTransactionsURL,
  myWalletBudgetsPendingURL,
  myWalletBudgetsURL,
  myWalletPhysicalCardsUrl,
  myWalletTransactionsURL,
  myWalletVirtualCardsUrl,
  orgExpenseReportsHistoricalReportsUrl,
  paymentsUrl,
  peopleInvitedToOrgUrl,
  peopleMembersUrl,
  reimbursementsUrl,
  searchUrl,
  submitExpensesUrl,
  vendorsUrl,
  virtualCardsUrl,
} from 'src/navigation/urls'
import { useQueryUserFeatures } from 'src/queries/auth.queries'
import { getCardFeedDisplayName } from 'src/queries/cardFeeds/cardFeeds.models'
import useQueryCardFeed from 'src/queries/cardFeeds/domain/useQueryCardFeed'
import { useQueryCreditCard } from 'src/queries/creditCard.queries'
import { expenseReportsQueries } from 'src/queries/expenseReports/expenseReports.queries'
import { useQueryAdditionalFields } from 'src/queries/organizations/additionalFields.queries'
import { useQueryDepartmentDetails } from 'src/queries/organizations/department.queries'
import { useQueryExpenseCategory } from 'src/queries/organizations/expenseCategories.queries'
import {
  useQueryOrgExpenseReport,
  useQueryOrgExpenseReportCycle,
} from 'src/queries/orgExpenseReports.queries'
import { useQueryOwnUser } from 'src/queries/users.queries'
import { useQueryVirtualCardDetail } from 'src/queries/virtualCards.queries'
import { selectIsSidenavLockedOpenFromState } from 'src/redux/selectors/app.selectors'
import { selectFixedElementPushdown } from 'src/redux/selectors/css.selectors'
import { selectUserExplicitOrganizationListModelFromState } from 'src/redux/selectors/data/organizations.selectors'
import {
  selectExpenseReportIdFromRoute as selectOrgExpenseReportIdFromRoute,
  selectExpenseReportsHistoricalReportsCycleIdFromRoute,
} from 'src/redux/selectors/data/orgExpenseReports.selectors'
import variables from 'src/style/variables.json'
import { css } from 'styled-components'

import CreateNewDropdown from '../CreateNewDropdown/CreateNewDropdown'

const TopBar: React.FC = (): React.ReactElement => {
  const history = useHistory<LocationState>()
  const fixedElementPushdown = useSelector(selectFixedElementPushdown)
  const { data: user } = useQueryOwnUser({})
  const userOrganization = useSelector(
    selectUserExplicitOrganizationListModelFromState,
  )
  const sideNavLockedOpen = useSelector(selectIsSidenavLockedOpenFromState)

  const { VIEW_VIRTUAL_CARD_FEATURES: hasVirtualCardFeatures } =
    useQueryUserFeatures([UserFeature.VIEW_VIRTUAL_CARD_FEATURES]).data || {}

  const {
    location: appLocation,
    params: queryParams,
    contentType,
  } = useExtendLocation()
  const homeMatch = appLocation === AppLocation.Home

  const rightShift = sideNavLockedOpen
    ? `${sidenavExpandedWidth}px`
    : `${sidenavCollapsedWidth}px`

  const handlePathClick = (url?: string): void => {
    if (url !== undefined) {
      history.replace(url)
    }
  }

  const translate = useTranslate()

  const renderPath = (crumb: BreadcrumbPathComponent): ReactNode => {
    return (
      <>
        {renderLink(
          crumb,
          <>
            {crumb.component !== undefined && <div>{crumb.component}</div>}
            {crumb.component === undefined && (
              <span
                css={css`
                  ${getFontCss(
                    12,
                    styleConstants.colors.gray_darkest,
                    styleConstants.fontFamilies.regular,
                  )};
                `}
              >
                {crumb.path}
              </span>
            )}
          </>,
        )}
        <span
          css={css`
            margin: 0 10px;
            ${getFontCss(
              14,
              styleConstants.colors.gray_darkest,
              styleConstants.fontFamilies.regular,
            )};
          `}
        >
          {'/'}
        </span>
      </>
    )
  }

  const renderTerminus = (crumb: BreadcrumbPathComponent): ReactNode => {
    return (
      <div
        css={css`
          ${getFontCss(
            14,
            themeConstants.colors.cta,
            styleConstants.fontFamilies.medium,
          )};
          display: flex;
          gap: 4px;
          justify-content: center;
          align-items: center;
          white-space: normal;
          padding-bottom: 5px;
          ${getBreakpointDown(breakpointSizes.XSM)(css`
            padding-bottom: 0px;
          `)}
        `}
      >
        {crumb.component !== undefined && <div>{crumb.component}</div>}
        {crumb.component === undefined && (
          <span
            css={css`
              ${getFontCss(
                12,
                themeConstants.colors.font_primary,
                styleConstants.fontFamilies.medium,
              )};
            `}
          >
            {crumb.path}
          </span>
        )}
      </div>
    )
  }

  const renderLink = (
    crumb: BreadcrumbPathComponent,
    children: ReactNode,
  ): ReactNode => {
    return (
      <HoverableLink
        onClick={(): void => handlePathClick(crumb.url)}
        ariaLabel={crumb.path ?? crumb.url ?? 'navigate'}
        colorSvgOnHover
        css={css`
          ${getFontCss(
            14,
            themeConstants.colors.cta,
            styleConstants.fontFamilies.medium,
          )};
          display: flex;
          gap: 4px;
          justify-content: center;
          align-items: center;
          white-space: normal;
          padding-bottom: 5px;
          ${getBreakpointDown(breakpointSizes.XSM)(css`
            padding-bottom: 0px;
          `)}
          cursor: ${crumb.url !== undefined ? 'pointer' : 'auto'};
        `}
      >
        {children}
      </HoverableLink>
    )
  }
  const { data: virtualCard } = useQueryVirtualCardDetail({
    virtualCardId: queryParams['id'],
    enabled: [
      AppLocation.MyWalletVirtualCardDetail,
      AppLocation.VirtualCardDetail,
    ].includes(appLocation),
  })

  const { data: departmentData } = useQueryDepartmentDetails({
    organizationId: queryParams['orgId'],
    departmentId: queryParams['departmentId'],
    enabled: appLocation === AppLocation.DepartmentDetails,
  })

  const { data: creditCard } = useQueryCreditCard({
    creditCardId: queryParams['id'],
    enabled: [
      AppLocation.AccountOverview,
      AppLocation.BudgetDetail,
      AppLocation.BudgetOverview,
      AppLocation.BudgetVirtualCards,
      AppLocation.BudgetTransactions,
      AppLocation.BudgetHistory,
      AppLocation.UpdateBudget,
      AppLocation.DeactivateBudget,
    ].includes(appLocation),
  })

  const reportId = useSelector(selectOrgExpenseReportIdFromRoute)
  const { data: report } = useQueryOrgExpenseReport({
    reportId,
  })

  const cycleId = useSelector(
    selectExpenseReportsHistoricalReportsCycleIdFromRoute,
  )
  const { data: cycle } = useQueryOrgExpenseReportCycle({
    cycleId,
  })

  // oope report
  const myWalletExpenseReportId = useSelector(
    selectMyWalletReimbursementIdFromRoute,
  )

  const managerReviewReimbursementId = useSelector(
    selectManagerReviewExpenseReportIdFromRoute,
  )
  const accountingReimbursementId = useSelector(selectExpenseReportIdFromRoute)

  const oopeReportId =
    myWalletExpenseReportId ??
    managerReviewReimbursementId ??
    accountingReimbursementId

  const { data: oopeReport } = useQuery(
    expenseReportsQueries.reportDetail(oopeReportId),
  )

  const { data: expenseCategory } = useQueryExpenseCategory({
    expenseCategoryId: queryParams['expenseCategoryId'],
    enabled:
      appLocation === AppLocation.OrganizationSettingsExpenseCategoryLabels,
    retry: false,
  })

  const { data: additionalFields } = useQueryAdditionalFields({
    filters: {},
    enabled: appLocation === AppLocation.OrganizationSettingsCustomFieldDetail,
  })
  const customField = additionalFields?.find(
    (field) => field.id === queryParams['customFieldId'],
  )

  const { data: cardFeed } = useQueryCardFeed(
    userOrganization?.id,
    queryParams['id'],
    appLocation === AppLocation.OrganizationSettingsCardFeedDetails,
  )

  const breadcrumbs: BreadcrumbPathComponent[] | undefined = useMemo(() => {
    switch (appLocation) {
      case AppLocation.MyWalletReimbursements:
        return [
          { path: translate('My Wallet') },
          { path: translate('Reimbursements'), url: submitExpensesUrl() },
        ]

      case AppLocation.MyWalletReimbursementDetail:
        return [
          { path: translate('My Wallet') },
          { path: translate('Reimbursements'), url: submitExpensesUrl() },
          { path: oopeReport?.reportName ?? translate('Report Detail') },
        ]

      case AppLocation.MyWalletTransactions:
        return [
          { path: translate('My Wallet') },
          { path: translate('Transactions'), url: myWalletTransactionsURL() },
        ]

      case AppLocation.MyWalletVirtualCards:
        return [
          { path: translate('My Wallet') },
          { path: translate('Virtual Cards'), url: myWalletVirtualCardsUrl() },
        ]

      case AppLocation.MyWalletVirtualCardDetail:
        return [
          { path: translate('My Wallet') },
          { path: translate('Virtual Cards'), url: myWalletVirtualCardsUrl() },
          { path: virtualCard?.displayName || translate('Detail') },
        ]

      case AppLocation.MyWalletPhysicalCards:
        return [
          { path: translate('My Wallet') },
          {
            path: translate('Physical Cards'),
            url: myWalletPhysicalCardsUrl(),
          },
        ]

      case AppLocation.MyWalletBudgets:
        return [
          { path: translate('My Wallet') },
          { path: translate('Budgets'), url: myWalletBudgetsURL() },
        ]

      case AppLocation.MyWalletBudgetsPending:
        return [
          { path: translate('My Wallet') },
          {
            path: translate('Pending Budgets'),
            url: myWalletBudgetsPendingURL(),
          },
        ]

      case AppLocation.PhysicalCards:
        return [
          { path: translate('Cards') },
          { path: translate('Physical Cards'), url: cardsPhysicalCardsUrl() },
        ]

      case AppLocation.AccountOverview:
      case AppLocation.AccountBudgets:
      case AppLocation.AccountVirtualCards:
      case AppLocation.AccountTransactions:
      case AppLocation.AccountBudgetHistory:
      case AppLocation.AccountSettings:
      case AppLocation.AccountStatements:
      case AppLocation.AccountActivity:
        return [
          { path: translate('Cards') },
          { path: translate('Physical Cards'), url: cardsPhysicalCardsUrl() },
          { path: creditCard?.displayName || translate('Detail') },
        ]

      case AppLocation.VirtualCardsAll:
      case AppLocation.VirtualCardsNeedsApproval:
        return [
          { path: translate('Cards') },
          { path: translate('Virtual Cards'), url: virtualCardsUrl() },
        ]

      case AppLocation.VirtualCardDetail:
        return [
          { path: translate('Cards') },
          { path: translate('Virtual Cards'), url: virtualCardsUrl() },
          { path: virtualCard?.displayName || translate('Detail') },
        ]

      case AppLocation.Budgets:
        return [{ path: translate('Budgets') }]

      case AppLocation.BudgetsAll:
        return [
          { path: translate('Budgets') },
          { path: translate('Overview'), url: budgetsAllBudgetsUrl() },
        ]

      case AppLocation.BudgetsNeedsApproval:
        return [
          { path: translate('Budgets') },
          { path: translate('Needs Approval'), url: budgetsNeedsApprovalUrl() },
        ]

      case AppLocation.BudgetDetail:
      case AppLocation.BudgetOverview:
      case AppLocation.BudgetVirtualCards:
      case AppLocation.BudgetTransactions:
      case AppLocation.BudgetHistory:
      case AppLocation.UpdateBudget:
      case AppLocation.DeactivateBudget:
        return [
          { path: translate('Budgets') },
          { path: translate('Overview'), url: budgetsAllBudgetsUrl() },
          { path: creditCard?.displayName || translate('Detail') },
        ]

      case AppLocation.BillPayAll:
        return [
          { path: translate('Cards') },
          { path: translate('Bill Pay'), url: billPayAllUrl() },
        ]

      case AppLocation.Payments:
        return [
          { path: translate('Bill Pay') },
          { path: translate('Payments'), url: paymentsUrl() },
        ]

      case AppLocation.Vendors:
        return [
          { path: translate('Bill Pay') },
          { path: translate('Vendors'), url: vendorsUrl() },
        ]

      case AppLocation.ManagerReviewTransactions:
        return [
          {
            path: translate('Manager Review'),
          },
          {
            path: translate('Card Transactions'),
            url: managerReviewTransactionsURL(),
          },
        ]

      case AppLocation.ManagerReviewReimbursements:
        return [
          {
            path: translate('Manager Review'),
          },
          {
            path: translate('Reimbursements'),
            url: managerReviewReimbursementsURL(),
          },
        ]

      case AppLocation.ManagerReviewReimbursementDetail:
        return [
          {
            path: translate('Manager Review'),
          },
          {
            path: translate('Reimbursements'),
            url: managerReviewReimbursementsURL(),
          },
          { path: oopeReport?.reportName || translate('Report Detail') },
        ]

      case AppLocation.AccountingReviewReports:
        return [
          { path: translate('Accounting') },
          { path: translate('Card Transactions') },
        ]

      case AppLocation.ReceiptRecovery:
        return [
          { path: translate('Accounting') },
          { path: translate('Receipt Recovery') },
        ]

      case AppLocation.AccountingReimbursements:
        return [
          { path: translate('Accounting') },
          { path: translate('Reimbursements'), url: reimbursementsUrl() },
        ]

      case AppLocation.ExpenseReport:
        return [
          { path: translate('Accounting') },
          { path: translate('Reimbursements'), url: reimbursementsUrl() },
          { path: oopeReport?.reportName || translate('Report Detail') },
        ]

      case AppLocation.AccountingExpenseReports:
        return [
          { path: translate('Accounting') },
          {
            path: translate('Statements'),
            url: expensesExpenseReportsUrl(),
          },
        ]

      case AppLocation.OrgExpenseReportsHistoricalReports:
        return [
          { path: translate('Accounting') },
          {
            path: translate('Statements'),
            url: expensesExpenseReportsUrl(),
          },
          {
            component: (
              <span
                css={css`
                  ${getFontCss(
                    12,
                    themeConstants.colors.font_primary,
                    styleConstants.fontFamilies.regular,
                  )};
                `}
              >
                <span
                  css={css`
                    padding-inline-end: 5px;
                  `}
                >
                  {cycle?.accountName}
                </span>
                {cycle?.accountLast4 && (
                  <Tag text={`${cycle.accountLast4}`} size={TagSizes.Small} />
                )}
              </span>
            ),
            url: orgExpenseReportsHistoricalReportsUrl(cycle?.id ?? ''),
          },
        ]

      case AppLocation.OrgExpenseReport:
        return [
          { path: translate('Accounting'), url: expensesExpenseReportsUrl() },
          {
            path: translate('Statements'),
            url: expensesExpenseReportsUrl(),
          },
          {
            component: (
              <span
                css={css`
                  ${getFontCss(
                    12,
                    themeConstants.colors.font_primary,
                    styleConstants.fontFamilies.regular,
                  )};
                `}
              >
                <span
                  css={css`
                    padding-inline-end: 5px;
                  `}
                >
                  {report?.accountName}
                </span>
                {report?.accountLast4 && (
                  <Tag text={`${report.accountLast4}`} size={TagSizes.Small} />
                )}
              </span>
            ),
            url: orgExpenseReportsHistoricalReportsUrl(report?.cycleId ?? ''),
          },
          {
            component: (
              <div
                css={css`
                  display: flex;
                  align-items: baseline;
                  gap: 5px;
                `}
              >
                <span
                  css={css`
                    ${getFontCss(
                      12,
                      themeConstants.colors.font_primary,
                      styleConstants.fontFamilies.medium,
                    )};
                  `}
                >
                  {`${moment.utc(report?.since).format('LL')} - ${moment
                    .utc(report?.until)
                    .format('LL')}`}
                </span>
              </div>
            ),
          },
        ]

      case AppLocation.OrganizationSettingsOrganizationInfo:
        return [
          { path: translate('Organization Settings') },
          { path: translate('General') },
          { path: translate('Organization Info') },
        ]

      case AppLocation.OrganizationSettingsBankAccounts:
        return [
          { path: translate('Organization Settings') },
          { path: translate('General') },
          { path: translate('Bank Account') },
        ]

      case AppLocation.OrganizationSettingsSSO:
        return [
          { path: translate('Organization Settings') },
          { path: translate('General') },
          { path: translate('SSO') },
        ]

      case AppLocation.OrganizationSettingsDepartments:
        return [
          { path: translate('Organization Settings') },
          { path: translate('General') },
          { path: translate('Teams') },
        ]

      case AppLocation.DepartmentDetails:
        return [
          { path: translate('Organization Settings') },
          { path: translate('General') },
          {
            path: translate('Teams'),
            url: userOrganization
              ? departmentsUrl(userOrganization.id)
              : undefined,
          },
          { path: departmentData?.name || translate('Detail') },
        ]

      case AppLocation.OrganizationSettingsManageSubscription:
        return [
          { path: translate('Organization Settings') },
          { path: translate('General') },
          { path: translate('Manage Plan') },
        ]

      case AppLocation.OrganizationSettingsAPIKeys:
        return [
          { path: translate('Organization Settings') },
          { path: translate('General') },
          { path: translate('API Key') },
        ]

      case AppLocation.OrganizationSettingsIntegrations:
      case AppLocation.OrganizationSettingsIntegrationsAccounting:
        return [
          { path: translate('Organization Settings') },
          { path: translate('Integrations') },
          { path: translate('Accounting') },
        ]

      case AppLocation.OrganizationSettingsIntegrationsProductivity:
        return [
          { path: translate('Organization Settings') },
          { path: translate('Integrations') },
          { path: translate('Productivity') },
        ]

      case AppLocation.OrganizationSettingsCardFeeds:
      case AppLocation.OrganizationSettingsCardFeedCreate:
        return [
          { path: translate('Organization Settings') },
          { path: translate('Integrations') },
          { path: translate('Card Feeds') },
        ]

      case AppLocation.OrganizationSettingsCardFeedDetails:
        return [
          { path: translate('Organization Settings') },
          { path: translate('Integrations') },
          { path: translate('Card Feeds'), url: cardFeedsUrl() },
          { path: getCardFeedDisplayName(cardFeed, translate) },
        ]

      case AppLocation.OrganizationSettingsExpenseCategories:
        return [
          { path: translate('Organization Settings') },
          { path: translate('Expense Categories') },
          { path: translate('Accounting Categories') },
        ]

      case AppLocation.OrganizationSettingsExpenseCategoriesAdditionalFields:
        return [
          { path: translate('Organization Settings') },
          {
            path: translate('Expense Categories'),
            url: userOrganization
              ? expenseCategoriesAccountingUrl(userOrganization.id)
              : undefined,
          },
          { path: translate('Custom Fields') },
        ]

      case AppLocation.OrganizationSettingsCustomFieldDetail:
        return [
          { path: translate('Organization Settings') },
          {
            path: translate('Expense Categories'),
            url: userOrganization
              ? expenseCategoriesAccountingUrl(userOrganization.id)
              : undefined,
          },
          {
            path: translate('Custom Fields'),
            url: userOrganization
              ? expenseCategoriesAdditionalFieldsUrl(userOrganization.id)
              : undefined,
          },
          { path: customField?.name || translate('Detail') },
        ]

      case AppLocation.OrganizationSettingsExpenseCategoryLabels:
        return [
          { path: translate('Organization Settings') },
          {
            path: translate('Expense Categories'),
            url: userOrganization
              ? expenseCategoriesAccountingUrl(userOrganization.id)
              : undefined,
          },
          { path: expenseCategory?.name || translate('Detail') },
        ]

      case AppLocation.OrganizationSettingsAutomationRules:
        return [
          { path: translate('Organization Settings') },
          { path: translate('Rules & Workflows') },
          { path: translate('Automation') },
          { path: translate('Accounting Rules') },
        ]

      case AppLocation.OrganizationSettingsExpenseSettings:
        return [
          { path: translate('Organization Settings') },
          { path: translate('Rules & Workflows') },
          { path: translate('Automation') },
          { path: translate('Report Cycle') },
        ]

      case AppLocation.OrganizationSettingsReceiptRules:
        return [
          { path: translate('Organization Settings') },
          { path: translate('Rules & Workflows') },
          { path: translate('Automation') },
          { path: translate('Receipt Rules') },
        ]

      case AppLocation.OrganizationSettingsReimbursements:
        return [
          { path: translate('Organization Settings') },
          { path: translate('Rules & Workflows') },
          { path: translate('Reimbursements') },
        ]

      case AppLocation.OrganizationSettingsBusinessRules:
        return [
          { path: translate('Organization Settings') },
          { path: translate('Rules & Workflows') },
          { path: translate('Approvers') },
        ]

      case AppLocation.OrganizationSettingsExpenseRequirements:
        return [
          { path: translate('Organization Settings') },
          { path: translate('Rules & Workflows') },
          { path: translate('Expense Requirements') },
        ]

      case AppLocation.OrganizationSettingsExpenseRequirementsReceiptRules:
        return [
          { path: translate('Organization Settings') },
          { path: translate('Rules & Workflows') },
          { path: translate('Expense Requirements') },
          { path: translate('Receipt Rules') },
        ]

      case AppLocation.OrganizationSettingsExpenseRequirementsAdditionalFields:
        return [
          { path: translate('Organization Settings') },
          { path: translate('Rules & Workflows') },
          { path: translate('Expense Requirements') },
          { path: translate('Custom Fields') },
        ]

      case AppLocation.PeopleMembers:
        return [
          { path: translate('People') },
          {
            path: translate('Organization Members'),
            url: peopleMembersUrl(),
          },
        ]

      case AppLocation.PeopleInvitedToOrg:
        return [
          { path: translate('People') },
          {
            path: translate('Invited To Org'),
            url: peopleInvitedToOrgUrl(),
          },
        ]

      default:
        return undefined
    }
  }, [
    appLocation,
    cycle,
    virtualCard,
    creditCard,
    report,
    oopeReport,
    expenseCategory,
    customField,
    cardFeed,
    departmentData,
    translate,
    userOrganization,
  ])

  return (
    <>
      <div
        css={css`
          top: ${fixedElementPushdown}px;
          z-index: 1;
          position: fixed;
          left: ${rightShift};
          height: 72px;
          width: calc(100% - ${rightShift});
        `}
      />
      <div
        css={css`
          min-height: 72px;
        `}
      />
      <div
        css={css`
          top: ${fixedElementPushdown}px;
          z-index: 5;
          position: fixed;
          left: ${rightShift};
          background-color: ${base_colorRaisedSurface};
          padding: ${spacing(6)};
          height: 72px;
          transition: width 200ms ease-out;
          display: flex;
          align-items: center;
          width: calc(100% - ${rightShift});
          margin: 0;
          border-radius: 0;
          border-bottom: 1px solid ${base_colorContentBorder};
          justify-content: end;
        `}
      >
        {breadcrumbs !== undefined && (
          <div
            css={css`
              display: flex;
              margin-right: auto;
            `}
          >
            {breadcrumbs.map((crumb, index) => {
              return (
                <div
                  key={index}
                  css={css`
                    display: flex;
                    min-width: 0;
                    flex-shrink: 2;
                  `}
                >
                  {index < breadcrumbs.length - 1 && renderPath(crumb)}
                  {index === breadcrumbs.length - 1 && renderTerminus(crumb)}
                </div>
              )
            })}
          </div>
        )}
        <div
          css={css`
            display: flex;
            align-items: center;
            min-width: 0;
          `}
        >
          <div
            css={css`
              margin-right: ${variables.units['base-unit-6']}px;
              align-items: center;
            `}
          >
            {hasVirtualCardFeatures && contentType !== 'Settings' && (
              <CreateNewDropdown isSecondaryCta={!homeMatch} />
            )}
          </div>
          {hasVirtualCardFeatures && (
            <TestId testId={'topNavSearch'}>
              <Lnk
                id="topSearchBtn"
                to={searchUrl()}
                ariaLabelSource={'Search Virtual Cards'}
                css={css`
                  display: flex;
                  align-items: center;
                  padding: 0px ${variables.units['base-unit-2']}px;
                `}
              >
                <SvgSearchTopBar />
              </Lnk>
            </TestId>
          )}
          {user !== undefined && (
            <TestId testId={'topNavProfile'}>
              <PopupMenu
                width={240}
                baseComponent={
                  <div
                    css={css`
                      display: flex;
                      align-items: center;
                      margin-left: ${variables.units['base-unit-2']}px;
                      gap: ${variables.units['base-unit-1']}px;
                    `}
                  >
                    <UserAvatar user={user} size={AvatarSizes.Small24} />
                    <div>
                      <TestId testId={'topNavOrganizationRole'}>
                        <Tag
                          css={`
                            padding: 0px ${variables.units['base-unit-1']}px;
                          `} // TODO: change in extend-web-shared
                          text={translate(user?.orgRoleDisplayTagTitle || '')}
                          size={TagSizes.Small}
                        />
                      </TestId>
                    </div>
                  </div>
                }
                menuComponent={
                  <ProfilePopupMenu
                    user={user}
                    userOrganization={userOrganization}
                  />
                }
                paddingTop={0}
                paddingBottom={5}
                topOffset={0}
                rightOffset={variables.units['base-unit-2']}
                closeOnAnyClick={false}
                closeOnSecondClick={false}
              />
            </TestId>
          )}
        </div>
      </div>
    </>
  )
}

export default TopBar
